import { FireAndForgetChannel } from './fireandforget';

export interface ProxiedFunctionCall {
  ServiceName: string;
  FuncName: string;
  Args: any[];
}

// Creates a Client to some Service running on the other side of the specified channel.
// TODO(jaime): Use code gen to create these clients, to avoid needing people to instantiate these service stubs.
export function CreateClient<S extends object>(
  ch: FireAndForgetChannel<ProxiedFunctionCall>,
  stub: S,
  nameOverride?: string,
): S {
  let serviceName = '';
  if (nameOverride) {
    serviceName = nameOverride;
  } else {
    serviceName = ClassName(stub);
    const idx = serviceName.indexOf('ClientStub');
    if (idx < 0) {
      throw `Client stub [${serviceName}]must be named *ClientStub`;
    }
    serviceName = serviceName.substring(0, idx);
  }

  const proxy: any = {};
  const stubMethods = stub as { [method: string]: Function | unknown };
  for (const k of Object.getOwnPropertyNames(Object.getPrototypeOf(stubMethods))) {
    if (typeof stubMethods[k] == 'function') {
      proxy[k] = proxyFunc(serviceName, k, ch);
    }
  }

  return proxy as S;
}

// Returns a proxyFunc that will glue a client call to a channel send.
function proxyFunc(
  serviceName: string,
  funcName: string,
  ch: FireAndForgetChannel<ProxiedFunctionCall>,
): Function {
  return function (...args: any[]) {
    const msg: ProxiedFunctionCall = {
      ServiceName: serviceName,
      FuncName: funcName,
      Args: args,
    };
    ch.send(msg); // Will automatically buffer if not connected.
  };
}

export class Dispatcher {
  services: { [key: string]: any } = {};
  _unhooker: () => void;

  constructor(private ch: FireAndForgetChannel<ProxiedFunctionCall>) {
    this._unhooker = this.ch.listen((m: ProxiedFunctionCall) => {
      const s = this.services[m.ServiceName];
      if (!s) {
        console.warn("Received ProxiedFunctionCall for Service that wasn't registered:", m);
        return;
      }
      this.dispatch(s, m);
    });
  }

  Ch(): FireAndForgetChannel<ProxiedFunctionCall> {
    return this.ch;
  }

  dispatch(s: any, m: ProxiedFunctionCall) {
    const f = s[m.FuncName];
    if (!f) {
      console.error(
        `No such function [${m.FuncName}] found on Service [${m.ServiceName}]. Unable to dispatch ProxiedFunctionCall:`,
        m,
      );
      return;
    }
    if (typeof f != 'function') {
      console.error(
        `[${m.FuncName}] found on Service [${m.ServiceName}] is NOT a function! Unable to dispatch ProxiedFunctionCall:`,
        m,
      );
      return;
    }
    f.apply(s, m.Args);
  }

  Shutdown() {
    this._unhooker();
  }

  // Registers a service on the inside
  RegisterService(s: any, nameOverride?: string) {
    let serviceName = '';
    if (nameOverride) {
      serviceName = nameOverride;
    } else {
      serviceName = ClassName(s);
      const idx = serviceName.indexOf('Impl');
      if (idx > 0) {
        // Optionally strip Impl from the end. Assume if it is missing, that the service Impl is named
        // exactly as we want to name the service.
        serviceName = serviceName.substring(0, idx);
      }
    }
    this.services[serviceName] = s;
  }
}

export function ClassName(clazz: any) {
  return clazz.constructor.name;
}
